import {
  assign, getSolutionQuality, unassign,
} from '../../SolverUtils/Util';

/**
 * branchAndBound.js
 * This file contains an implementation of the branch and bound backtracking search algorithm.
 */

let constraintMap;

/**
 * Checks if the assignment of value v1 to variable var1 breaks a constraint between var1 and var2 when var2 is assigned value v2
 * @param {*} var1 The first variable
 * @param {*} v1 The value to be assigned to var1
 * @param {*} var2 The second variable
 * @param {*} v2 The value to be assigned to var2
 * @returns {boolean} Whether the variable-value-pairs break the constraint
 */
function check(var1, v1, var2, v2) {
  let found = false;
  let var1var2Ord = true;
  let con;
  // Look for constraint between the variables
  if (
    constraintMap.get(`${var1.variableName} ${var2.variableName}`) !== undefined
  ) {
    con = constraintMap.get(`${var1.variableName} ${var2.variableName}`);
    found = true;
  } else if (
    constraintMap.get(`${var2.variableName} ${var1.variableName}`) !== undefined
  ) {
    con = constraintMap.get(`${var2.variableName} ${var1.variableName}`);
    found = true;
    var1var2Ord = false;
  }
  if (!found) {
    return true;
  }
  if (var1var2Ord) {
    let constraintBroken = false;
    let i = 0;
    while (!constraintBroken && i < con.length) {
      if (!con[i].definition(v1, v2)) {
        constraintBroken = true;
      }
      i++;
    }
    return !constraintBroken;
  }
  let constraintBroken = false;
  let i = 0;
  while (!constraintBroken && i < con.length) {
    if (!con[i].definition(v2, v1)) {
      constraintBroken = true;
    }
    i++;
  }
  return !constraintBroken;
}

/**
 * Updates the domain of the variable by removing values that are inconsistent with the assignment.
 * @param {*} variable - The variable whose domain should be updated.
 * @param {*} assignedVar - The assigned variable.
 * @param {*} assignedValue - The value assigned to the assignedVar.
 * @returns {Array|null} - the variable's current active domain, possibly updated
 */
function updateDomain(variable, assignedVar, assignedValue) {
  const originalDomain = variable.currentDomain.getActiveApplicants();
  const { variableName } = variable;

  originalDomain.forEach((value) => {
    if (!check(assignedVar, assignedValue, variable, value)) {
      variable.currentDomain.removeApplicant(value);
      // add value to the reductions map
      if (assignedVar.reductions.has(variableName)) {
        assignedVar.reductions.set(variableName, [
          ...assignedVar.reductions.get(variableName),
          value,
        ]);
      } else {
        assignedVar.reductions.set(variableName, [value]);
      }
    }
  });

  return variable.currentDomain.getActiveApplicants();
}

export function branchAndBound(cspInstance) {
  let bestLen = 0;
  let bestSol;
  let bestQual = -Infinity;
  constraintMap = cspInstance.constraintMap;
  const {
    variables, variableMap, intervals, taskIntervalMap,
  } = cspInstance;

  // Initialize the stack.
  const stack = [
    {
      /** index of variable in the partial solution */
      variableIndex: 0,
      /** index of variable in the variables array */
      varIndex: 0,
    },
  ];
  while (stack.length > 0) {
    const frame = stack.pop();

    if (frame.variableIndex === variables.length) {
      // All variables have been assigned, a solution has been found.
      return variables.map((variable) => ({
        variable: variable.variableName,
        assignment: variable.currentDomain.getAssignment(),
      }));
    }
    const variable = variables[frame.varIndex];
    const activeApplicants = variable.currentDomain.getActiveApplicants();

    const currQual = getSolutionQuality(intervals);
    if (activeApplicants.length > 0 && currQual >= bestQual) {
      // Assign a value to the current variable
      const value = activeApplicants[0];
      variable.currentDomain.assign(value);

      // -------------------
      const newSol = variables
        .map((varb) => ({
          variable: varb.variableName,
          assignment: varb.currentDomain.getAssignment(),
        }))
        .filter((assignment) => assignment.assignment !== undefined)
        .slice();
      const newLen = newSol.length;
      if (newLen > bestLen) {
        bestLen = newLen;
        bestSol = newSol;
      }
      if (bestLen === variables.length) {
        return variables.map((varb) => ({
          variable: varb.variableName,
          assignment: varb.currentDomain.getAssignment(),
        }));
      }
      // -------------------

      // Apply FC filtering to the future variables and check if all their domains are wiped out.
      let allWipedOut = true;

      // update domains for all future variables

      for (let i = frame.varIndex + 1; i < variables.length; i++) {
        const updatedDomain = updateDomain(variables[i], variable, value);

        // if the updatedDomain has some values still active, set allWipedOut to false
        if (updatedDomain.length !== 0) {
          allWipedOut = false;
        }
      }

      // interval assign
      // =-=-=-=-=-=-=-=-=-=

      // Get the task name without the "-#" part
      const taskName = variable.variableName.split('-')[0];

      // Get the filteredTasks based on the assignment
      const uniqueTasks = [];
      const set = [];
      Array.from(variable.reductions.keys()).forEach((key) => {
        const shortName = key.split('-')[0];
        if (!(set.includes(shortName))) {
          set.push(shortName);
          uniqueTasks.push(key);
        }
      });
      const filteredTasks = uniqueTasks.map(
        (key) => ({
          name: key.split('-')[0],
          amount: variable.reductions.get(key).length,
        })
      );

      // Update intervals and intervalTaskMap with the new assignment
      assign(taskName, filteredTasks, intervals, taskIntervalMap);
      // =-=-=-=-=-=-=-=-=-=

      if (!allWipedOut) {
        // Not all future variables are wiped out, push the current variable and its domain to the stack and move to the next variable.
        stack.push(frame);

        let nextVariable;
        // find next variable that is not wiped out
        for (let i = frame.varIndex + 1; i < variables.length; i++) {
        // variable with active domain values
          if (variables[i].currentDomain.getActiveApplicants().length > 0) {
            nextVariable = [variables[i], i];
            break;
          }
        }

        // push next variable onto the stack
        if (nextVariable !== undefined) {
          stack.push({
            variableIndex: frame.variableIndex + 1,
            varIndex: nextVariable[1],
          });
        }
      } else {
        // all future variables are wiped out due to the given assignment

        if (currQual > bestQual) {
          bestQual = currQual;
        }

        // remove assignment and remove value from domain
        variable.currentDomain.unassign();
        // push value to removedValues
        variable.removedValues.push(value);

        // Restore the wiped out future variables' domains.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of variable.reductions) {
          variableMap.get(key).currentDomain.addApplicants(val);
        }

        // =-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=

        // get filteredVars based on unassignment
        const filteredVars = [];
        const set2 = [];

        // Restore the wiped out future variables' domains done by the previous variable.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of variable.reductions) {
          const shortName = key.split('-')[0];
          if (!(shortName in set2)) {
            set2.push(shortName);
            filteredVars.push({
              name: shortName,
              amount: val.length,
            });
          }
          val.forEach((applicant) => {
            variableMap.get(key).currentDomain.addApplicant(applicant);
          });
        }
        // clear the previous variable's reductions map
        variable.reductions.clear();
        // =-=-=-=-=-=-=-=-=-=

        // Get the task name without the "-#" part
        const taskName2 = variable.variableName.split('-')[0]; // ??? variable or prevVar

        // Update intervals and intervalTaskMap with the new unassignment
        unassign(taskName2, filteredVars, intervals, taskIntervalMap);

        // =-=-=-=-=-=-=-=-=-=

        // clear the variable's reductions map
        variable.reductions.clear();

        // Continue with the next value of the current variable.
        stack.push(frame);
      }
    } else {
      // no values to assign to current variable meaning everything is wiped out (backtracking condition)

      // if there are previous variables
      if (stack.length > 0) {
        // add domain values back that were removed (retaining the filtering done by previous variables)
        variable.currentDomain.addApplicants(variable.removedValues);
        variable.removedValues = [];

        const prevFrame = stack.pop();
        const prevVar = variables[prevFrame.varIndex];

        // push assigned value to removed values
        prevVar.removedValues.push(prevVar.currentDomain.getAssignment());
        // remove assignment from previous variable and its domain
        prevVar.currentDomain.unassign();

        // =-=-=-=-=-=-=-=-=-=-=-=-=-=-=-=

        // get filteredVars based on unassignment
        const filteredVars = [];
        const set = [];

        // Restore the wiped out future variables' domains done by the previous variable.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of prevVar.reductions) {
          const shortName = key.split('-')[0];
          if (!(shortName in set)) {
            set.push(shortName);
            filteredVars.push({
              name: shortName,
              amount: val.length,
            });
          }
          val.forEach((applicant) => {
            variableMap.get(key).currentDomain.addApplicant(applicant);
          });
        }
        // clear the previous variable's reductions map
        prevVar.reductions.clear();

        // =-=-=-=-=-=-=-=-=-=

        // Get the task name without the "-#" part
        const taskName = prevVar.variableName.split('-')[0]; // ??? variable or prevVar

        // Get the filteredVars based on the unassignment

        // Update intervals and intervalTaskMap with the new unassignment
        unassign(taskName, filteredVars, intervals, taskIntervalMap);

        // =-=-=-=-=-=-=-=-=-=

        // Restore the wiped out future variables' domains done by the previous variable.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of prevVar.reductions) {
          variableMap.get(key).currentDomain.addApplicants(val);
        }
        // clear the previous variable's reductions map
        prevVar.reductions.clear();

        // try next value of previous variable
        stack.push(prevFrame);
      }
    }
  }

  return bestSol;
}
